import { useEffect, useId, useRef, useState } from "react";
import { LocateFixed, Search, X } from "lucide-react";
import { searchPlaces, type LocationOption } from "../locationSearch";
import "./map-experience.css";

export function LocationSearch({
  area,
  isDemo,
  onSelect,
  onLocate,
}: {
  area: string;
  isDemo: boolean;
  onSelect: (option: LocationOption) => void;
  onLocate: () => void;
}) {
  const [query, setQuery] = useState(""),
    [options, setOptions] = useState<LocationOption[]>([]);
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [active, setActive] = useState(-1);
  const sequence = useRef(0),
    input = useRef<HTMLInputElement>(null),
    listId = useId();
  const debounce = useRef<number | undefined>(undefined),
    focused = useRef(false);
  function close() {
    sequence.current++;
    clearTimeout(debounce.current);
    setOpen(false);
    setActive(-1);
    setBusy(false);
  }
  function select(option: LocationOption) {
    sequence.current++;
    setOpen(false);
    setBusy(false);
    setQuery("");
    setActive(-1);
    onSelect(option);
    input.current?.focus();
  }
  async function find(value: string, selectSingle = false) {
    const generation = ++sequence.current;
    if (isDemo) {
      setOpen(true);
      setError(
        "The sample hunt is in Chicago. Open the live map to choose another area.",
      );
      return;
    }
    if (value.trim().length < 2) {
      setOptions([]);
      setBusy(false);
      return;
    }
    setOpen(true);
    setBusy(true);
    setError("");
    setActive(-1);
    try {
      const matches = await searchPlaces(value);
      if (generation !== sequence.current) return;
      setOptions(matches);
      if (selectSingle && matches.length === 1) select(matches[0]);
    } catch (cause) {
      if (generation === sequence.current) {
        setOptions([]);
        setError(
          cause instanceof Error
            ? cause.message
            : "We couldn’t load places. Please try again.",
        );
      }
    } finally {
      if (generation === sequence.current) setBusy(false);
    }
  }
  useEffect(() => {
    sequence.current++;
    debounce.current = window.setTimeout(() => {
      if (focused.current && query.trim().length >= 2) void find(query);
      else {
        setOptions([]);
        setOpen(false);
        setBusy(false);
      }
    }, 220);
    return () => clearTimeout(debounce.current);
  }, [query, isDemo]);
  useEffect(
    () => () => {
      sequence.current++;
    },
    [],
  );
  return (
    <div
      className="location-combobox"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          focused.current = false;
          close();
        }
      }}
    >
      <form
        className="location-search"
        onSubmit={(event) => {
          event.preventDefault();
          if (active >= 0 && options[active]) select(options[active]);
          else void find(query, true);
        }}
      >
        <button type="submit" aria-label="Search area">
          <Search size={18} />
        </button>
        <input
          ref={input}
          role="combobox"
          aria-label="Search city or ZIP"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && active >= 0 ? `${listId}-${active}` : undefined
          }
          placeholder={`City or ZIP · ${area}`}
          value={query}
          onChange={(event) => {
            sequence.current++;
            clearTimeout(debounce.current);
            setOptions([]);
            setActive(-1);
            setBusy(false);
            setQuery(event.target.value);
          }}
          onFocus={() => {
            focused.current = true;
            if (query.length >= 2) void find(query);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") close();
            if (
              ["ArrowDown", "ArrowUp"].includes(event.key) &&
              options.length
            ) {
              event.preventDefault();
              setOpen(true);
              setActive((index) =>
                index < 0
                  ? event.key === "ArrowDown"
                    ? 0
                    : options.length - 1
                  : (index +
                      (event.key === "ArrowDown" ? 1 : -1) +
                      options.length) %
                    options.length,
              );
            }
          }}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear location search"
            onClick={() => {
              setQuery("");
              input.current?.focus();
            }}
          >
            <X size={15} />
          </button>
        )}
        <button
          type="button"
          aria-label="Use my location"
          title="Use my location"
          onClick={onLocate}
        >
          <LocateFixed size={18} />
        </button>
      </form>
      {open && (
        <div className="location-choices" id={listId}>
          {busy ? (
            <p className="location-search-message" role="status">
              Finding your area…
            </p>
          ) : error ? (
            <p className="location-search-message" role="status">
              {error}
              {isDemo && (
                <>
                  <br />
                  <a href="/">Open the live map</a>
                </>
              )}
            </p>
          ) : options.length ? (
            <ul role="listbox" aria-label="Matching places">
              {options.map((option, index) => (
                <li
                  key={option.id}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  className={`location-choice${index === active ? " active" : ""}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => select(option)}
                >
                  {option.label}
                  <small>
                    {option.kind === "zip"
                      ? "Search around this ZIP area"
                      : "Search nearby stores"}
                  </small>
                </li>
              ))}
            </ul>
          ) : (
            <p className="location-search-message" role="status">
              No area found. Try a city and state, or a five-digit ZIP.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
